/**
 * Prüfabläufe pflegen.
 *
 * Ein Ablauf ist die Schrittfolge, die der Prüfer an einem Gerätetyp abgeht –
 * bei einer Schaukel etwa Aufhängung sichten, Kettenverschleiß messen,
 * Fallschutzdicke messen. Das ist Bernds fachliches Kapital und ändert sich
 * mit der Erfahrung. Bisher ließ es sich nur beim Einrichten festlegen; das
 * war der Punkt, an dem er auf uns angewiesen war.
 *
 * Die Messschritte sind der eigentliche Grund für den Aufwand: aus Sollwert
 * und Einheit leitet der Prüfbogen selbst ab, ob ein Messwert abweicht, und
 * schlägt eine Mängelstufe vor. Ein Messschritt ohne Sollbereich könnte das
 * nicht – das weist der Server ab, und das Formular hier ebenso.
 */

import { useState } from 'react'
import {
  SCHRITTARTHILFE,
  SCHRITTARTTEXT,
  sollbereichText,
  useSchrittAendern,
  useSchrittAnlegen,
  useSchrittLoeschen,
  useTunnelvorlagen,
  useVorlageAendern,
  useVorlageAnlegen,
  type Schrittart,
  type Tunnelschritt,
  type Tunnelvorlage,
} from '../api/tunnel'
import { useGeraetetypen } from '../api/abfragen'
import { STUFENTEXT, type Stufe } from '../api/typen'
import { Blatt } from '../bausteine/Blatt'
import { Fehlerhinweis, Ladeanzeige, Leerhinweis } from '../bausteine/Hinweise'
import { darfSchreiben, useKonto } from '../konto/KontoKontext'

const ARTEN: Schrittart[] = ['hinweis', 'sicht', 'foto', 'messwert', 'bestaetigung']
const STUFEN: Stufe[] = ['1', '2', '3']

export function Tunnelvorlagen() {
  const { konto } = useKonto()
  const vorlagen = useTunnelvorlagen()
  const [neuOffen, setzeNeuOffen] = useState(false)
  const [offen, setzeOffen] = useState<string | null>(null)

  const darf = darfSchreiben(konto)

  if (vorlagen.isLoading) return <Ladeanzeige />
  if (vorlagen.error) return <Fehlerhinweis fehler={vorlagen.error} />

  const liste = vorlagen.data ?? []

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-center gap-3">
        <h1 className="text-2xl font-bold">Prüfabläufe</h1>
        {darf && (
          <button
            type="button"
            className="knopf-haupt ml-auto"
            onClick={() => setzeNeuOffen(true)}
          >
            Neuer Ablauf
          </button>
        )}
      </header>

      <p className="text-base text-slate-700">
        Ein Ablauf führt beim Prüfen Schritt für Schritt durch ein Gerät. Messschritte
        mit Sollbereich schlagen bei Abweichung selbst eine Mängelstufe vor.
      </p>

      {liste.length === 0 ? (
        <Leerhinweis titel="Noch keine Abläufe" />
      ) : (
        <ul className="space-y-2">
          {liste.map((v) => (
            <li key={v.id} className="karte">
              <button
                type="button"
                className="flex w-full items-center gap-3 text-left"
                onClick={() => setzeOffen(offen === v.id ? null : v.id)}
                aria-expanded={offen === v.id}
              >
                <div className="flex-1">
                  <div className="font-semibold">{v.name}</div>
                  <div className="text-base text-slate-700">
                    {v.geraetetyp ?? (v.normteil ? `alle nach ${v.normteil}` : 'alle übrigen Geräte')} · {v.schritte.length}{' '}
                    {v.schritte.length === 1 ? 'Schritt' : 'Schritte'}
                    {!v.aktiv ? ' · stillgelegt' : ''}
                  </div>
                </div>
                <span aria-hidden className="text-2xl">
                  {offen === v.id ? '−' : '+'}
                </span>
              </button>

              {offen === v.id && <Schritte vorlage={v} darf={darf} />}
            </li>
          ))}
        </ul>
      )}

      {neuOffen && (
        <VorlageBlatt
          vorlage={null}
          schliessen={() => setzeNeuOffen(false)}
          beimAngelegt={(neu) => {
            setzeNeuOffen(false)
            // Direkt aufklappen: ein Ablauf ohne Schritte nützt nichts,
            // der nächste Handgriff ist ohnehin der erste Schritt.
            setzeOffen(neu.id)
          }}
        />
      )}
    </div>
  )
}


/**
 * Das Blatt für einen Ablauf – dasselbe zum Anlegen wie zum Ändern.
 *
 * Vorher gab es das Formular nur beim Anlegen. Wer sich beim Gerätetyp
 * vertan hatte, konnte den Ablauf nur stilllegen und neu bauen, samt aller
 * Schritte. Genau das ist passiert: ein Ablauf zeigte auf einen Gerätetyp,
 * den es im Katalog nicht gab, und griff deshalb nie – ohne dass irgendwo
 * etwas darauf hinwies.
 */
function VorlageBlatt({
  vorlage,
  schliessen,
  beimAngelegt,
}: {
  vorlage: Tunnelvorlage | null
  schliessen: () => void
  beimAngelegt?: (neu: Tunnelvorlage) => void
}) {
  const anlegen = useVorlageAnlegen()
  const aendern = useVorlageAendern()
  const typen = useGeraetetypen()

  /* Die Normen aus dem Gerätetypkatalog, ohne Doppelte. So steht in der
     Auswahl genau das, was auch wirklich vergeben ist. */
  const normen = [
    ...new Set((typen.data ?? []).map((t) => t.normteil).filter(Boolean)),
  ].sort() as string[]

  // Beim Ändern steht die Auswahl auf dem, was der Ablauf heute ist.
  const [gilt, setzeGilt] = useState<'typ' | 'norm' | 'alle'>(
    vorlage ? (vorlage.geraetetyp ? 'typ' : vorlage.normteil ? 'norm' : 'alle') : 'typ',
  )

  const laeuft = anlegen.isPending || aendern.isPending

  return (
    <Blatt
      titel={vorlage ? 'Ablauf ändern' : 'Neuer Prüfablauf'}
      untertitel={vorlage?.name}
      schliessen={() => {
        anlegen.reset()
        aendern.reset()
        schliessen()
      }}
      fuss={
        <div className="flex gap-3">
          <button type="button" className="knopf-still flex-1" onClick={schliessen}>
            Abbrechen
          </button>
          <button
            type="submit"
            form="vorlage"
            className="knopf-haupt flex-1"
            disabled={laeuft}
          >
            {laeuft ? 'Speichert …' : vorlage ? 'Speichern' : 'Anlegen'}
          </button>
        </div>
      }
    >
      <form
        id="vorlage"
        className="space-y-3"
        onSubmit={(e) => {
          e.preventDefault()
          const f = new FormData(e.currentTarget)
          const text = (name: string) => String(f.get(name) ?? '').trim()

          /* Genau eines von beidem, oder keines für den allgemeinen Ablauf.
             Beim Ändern müssen die nicht gewählten Felder ausdrücklich auf
             null – sonst bliebe der alte Gerätetyp stehen, während die
             Oberfläche „alle übrigen Geräte" anzeigt. */
          const gilt_fuer = {
            geraetetyp: gilt === 'typ' ? text('geraetetyp') || null : null,
            normteil: gilt === 'norm' ? text('normteil') || null : null,
          }

          if (vorlage) {
            aendern.mutate(
              {
                id: vorlage.id,
                name: text('name'),
                beschreibung: text('beschreibung') || null,
                ...gilt_fuer,
              },
              { onSuccess: schliessen },
            )
          } else {
            anlegen.mutate(
              {
                name: text('name'),
                geraetetyp: gilt_fuer.geraetetyp ?? undefined,
                normteil: gilt_fuer.normteil ?? undefined,
                beschreibung: text('beschreibung') || undefined,
              },
              { onSuccess: (neu) => beimAngelegt?.(neu) },
            )
          }
        }}
      >
        <div>
          <label htmlFor="v-name">Name</label>
          <input
            id="v-name"
            name="name"
            required
            maxLength={120}
            defaultValue={vorlage?.name ?? ''}
          />
        </div>
        {/* Für wen der Ablauf gilt. Drei Möglichkeiten statt eines
            Freitextfelds: ein Tippfehler im Gerätetyp führte sonst zu
            einem Ablauf, der nie greift, ohne dass es jemand merkt. */}
        <fieldset className="space-y-2">
          <legend className="mb-1 block font-semibold text-slate-800">
            Für welche Geräte gilt der Ablauf?
          </legend>

          {(
            [
              ['typ', 'Einen bestimmten Gerätetyp', 'Etwa nur für Doppelschaukeln.'],
              [
                'norm',
                'Alle Geräte einer Norm',
                'Etwa alle Fitnessgeräte nach DIN EN 16630 – ein Ablauf statt zehn.',
              ],
              [
                'alle',
                'Alle übrigen Geräte',
                'Der allgemeine Ablauf, wenn nichts Genaueres passt.',
              ],
            ] as const
          ).map(([wert, text, erklaerung]) => (
            <label
              key={wert}
              className={`flex cursor-pointer items-start gap-3 rounded-feld border-2 p-2 font-normal ${
                gilt === wert ? 'border-akzent-500 bg-akzent-grund' : 'border-slate-200'
              }`}
            >
              <input
                type="radio"
                name="gilt"
                checked={gilt === wert}
                onChange={() => setzeGilt(wert)}
                className="mt-1 h-6 w-6 shrink-0"
                style={{ minHeight: 'auto' }}
              />
              <span>
                <span className="font-semibold">{text}</span>
                <span className="block text-base text-slate-600">{erklaerung}</span>
              </span>
            </label>
          ))}
        </fieldset>

        {gilt === 'typ' && (
          <div>
            <label htmlFor="v-typ">Gerätetyp</label>
            <select
              id="v-typ"
              name="geraetetyp"
              defaultValue={vorlage?.geraetetyp ?? ''}
            >
              <option value="">— wählen —</option>
              {(typen.data ?? []).map((typ) => (
                <option key={typ.id} value={typ.name}>
                  {typ.name}
                </option>
              ))}
            </select>
            <p className="mt-1 text-base text-slate-600">
              Aus dem Katalog gewählt, nicht getippt: ein Tippfehler ergäbe
              einen Ablauf, der nie greift.
            </p>
            {/* Der Fall, der schon einmal eingetreten ist: der gespeicherte
                Typ steht in keinem Katalogeintrag mehr. Die Auswahl fiele
                still auf leer zurück, und beim Speichern verschwände die
                Zuordnung, ohne dass jemand es bemerkt. */}
            {vorlage?.geraetetyp &&
              typen.data &&
              !typen.data.some((t) => t.name === vorlage.geraetetyp) && (
                <p className="mt-1 text-base font-semibold text-stufe-2">
                  Der bisher hinterlegte Typ „{vorlage.geraetetyp}" steht nicht im
                  Katalog. Der Ablauf greift deshalb bei keinem Gerät. Bitte
                  wählen Sie einen Typ aus der Liste.
                </p>
              )}
          </div>
        )}

        {gilt === 'norm' && (
          <div>
            <label htmlFor="v-norm">Norm</label>
            <select id="v-norm" name="normteil" defaultValue={vorlage?.normteil ?? ''}>
              <option value="">— wählen —</option>
              {normen.map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
            <p className="mt-1 text-base text-slate-600">
              Gilt für jedes Gerät, dessen Typ im Katalog diese Norm trägt.
            </p>
          </div>
        )}
        <div>
          <label htmlFor="v-beschreibung">Beschreibung</label>
          <textarea
            id="v-beschreibung"
            name="beschreibung"
            rows={2}
            defaultValue={vorlage?.beschreibung ?? ''}
          />
        </div>
        <Fehlerhinweis fehler={anlegen.error} />
        <Fehlerhinweis fehler={aendern.error} />
      </form>
    </Blatt>
  )
}

function Schritte({ vorlage, darf }: { vorlage: Tunnelvorlage; darf: boolean }) {
  const anlegen = useSchrittAnlegen(vorlage.id)
  const aendern = useVorlageAendern()
  const loeschen = useSchrittLoeschen()
  const umsortieren = useSchrittAendern()
  const [bearbeitet, setzeBearbeitet] = useState<Tunnelschritt | 'neu' | null>(null)
  const [ablaufOffen, setzeAblaufOffen] = useState(false)

  /* Reihenfolge ändern.
     
     Der Ablauf ist eine Reihenfolge, keine Menge: am Gerät geht man von
     oben nach unten, und ein Schritt, der zu früh kommt, verlangt eine
     Feststellung, die erst später möglich ist.
     
     Es werden nicht zwei Werte getauscht, sondern alle neu vergeben. Ein
     Tausch tut nichts, wenn zwei Schritte versehentlich dieselbe
     Sortierung tragen – und genau dann ist die Reihenfolge willkürlich
     und muss ohnehin gerichtet werden. Zehnerschritte, damit sich später
     etwas dazwischen schieben lässt. */
  async function verschieben(index: number, richtung: -1 | 1) {
    const ziel = index + richtung
    const liste = [...vorlage.schritte]
    if (ziel < 0 || ziel >= liste.length) return

    const [bewegt] = liste.splice(index, 1)
    liste.splice(ziel, 0, bewegt)

    for (const [i, schritt] of liste.entries()) {
      const neueSortierung = (i + 1) * 10
      if (schritt.sortierung === neueSortierung) continue
      await umsortieren.mutateAsync({ id: schritt.id, sortierung: neueSortierung })
    }
  }

  return (
    <div className="mt-3 border-t-2 border-slate-200 pt-3">
      {vorlage.beschreibung && (
        <p className="mb-2 text-base text-slate-700">{vorlage.beschreibung}</p>
      )}

      <ol className="space-y-2">
        {vorlage.schritte.map((s, i) => (
          <li key={s.id} className="flex items-start gap-3 rounded-feld bg-slate-50 p-2">
            <span className="mt-0.5 font-bold text-slate-500">{i + 1}</span>
            <div className="flex-1">
              <div className="font-semibold">{s.titel}</div>
              <div className="text-base text-slate-700">
                {SCHRITTARTTEXT[s.art]}
                {s.art === 'messwert' ? ` · ${sollbereichText(s)}` : ''}
                {!s.pflicht ? ' · freiwillig' : ''}
                {s.normverweis ? ` · ${s.normverweis}` : ''}
              </div>
            </div>
            {darf && (
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  className="knopf-still px-2 text-base"
                  aria-label={`Schritt „${s.titel}" nach oben`}
                  disabled={i === 0 || umsortieren.isPending}
                  onClick={() => void verschieben(i, -1)}
                >
                  ↑
                </button>
                <button
                  type="button"
                  className="knopf-still px-2 text-base"
                  aria-label={`Schritt „${s.titel}" nach unten`}
                  disabled={i === vorlage.schritte.length - 1 || umsortieren.isPending}
                  onClick={() => void verschieben(i, 1)}
                >
                  ↓
                </button>
                <button
                  type="button"
                  className="knopf-still px-3 text-base"
                  onClick={() => setzeBearbeitet(s)}
                >
                  Ändern
                </button>
              </div>
            )}
          </li>
        ))}
      </ol>

      {darf && (
        <div className="mt-3 flex flex-wrap gap-2">
          <button
            type="button"
            className="knopf-zweit px-3"
            onClick={() => setzeBearbeitet('neu')}
          >
            Schritt hinzufügen
          </button>
          <button
            type="button"
            className="knopf-zweit px-3"
            onClick={() => setzeAblaufOffen(true)}
          >
            Ablauf ändern
          </button>
          <button
            type="button"
            className="knopf-still px-3"
            onClick={() =>
              aendern.mutate({ id: vorlage.id, aktiv: !vorlage.aktiv })
            }
          >
            {vorlage.aktiv ? 'Stilllegen' : 'Wieder verwenden'}
          </button>
        </div>
      )}

      <Fehlerhinweis fehler={aendern.error} />
      <Fehlerhinweis fehler={loeschen.error} />
      <Fehlerhinweis fehler={umsortieren.error} />

      {ablaufOffen && (
        <VorlageBlatt vorlage={vorlage} schliessen={() => setzeAblaufOffen(false)} />
      )}

      {bearbeitet && (
        <SchrittBlatt
          schritt={bearbeitet === 'neu' ? null : bearbeitet}
          naechsteSortierung={(vorlage.schritte.length + 1) * 10}
          schliessen={() => setzeBearbeitet(null)}
          anlegen={anlegen}
          loeschen={loeschen}
        />
      )}
    </div>
  )
}

function SchrittBlatt({
  schritt,
  naechsteSortierung,
  schliessen,
  anlegen,
  loeschen,
}: {
  schritt: Tunnelschritt | null
  naechsteSortierung: number
  schliessen: () => void
  anlegen: ReturnType<typeof useSchrittAnlegen>
  loeschen: ReturnType<typeof useSchrittLoeschen>
}) {
  const aendern = useSchrittAendern()
  const [art, setzeArt] = useState<Schrittart>(schritt?.art ?? 'sicht')
  const laeuft = anlegen.isPending || aendern.isPending || loeschen.isPending

  return (
    <Blatt
      titel={schritt ? 'Schritt ändern' : 'Neuer Schritt'}
      untertitel={schritt?.titel}
      schliessen={schliessen}
      breit
      fuss={
        <div className="flex gap-3">
          <button type="button" className="knopf-still flex-1" onClick={schliessen}>
            Abbrechen
          </button>
          <button
            type="submit"
            form="schritt"
            className="knopf-haupt flex-1"
            disabled={laeuft}
          >
            {laeuft ? 'Speichert …' : 'Speichern'}
          </button>
        </div>
      }
    >
      <form
        id="schritt"
        className="space-y-3"
        onSubmit={(e) => {
          e.preventDefault()
          const f = new FormData(e.currentTarget)
          const text = (name: string) => String(f.get(name) ?? '').trim()
          const zahl = (name: string) => (text(name) === '' ? null : Number(text(name)))

          const nutzlast: Record<string, unknown> = {
            titel: text('titel'),
            hinweis: text('hinweis') || null,
            pflicht: f.get('pflicht') === 'on',
            normverweis: text('normverweis') || null,
            messgroesse: art === 'messwert' ? text('messgroesse') || null : null,
            einheit: art === 'messwert' ? text('einheit') || null : null,
            sollwert_min: art === 'messwert' ? zahl('sollwert_min') : null,
            sollwert_max: art === 'messwert' ? zahl('sollwert_max') : null,
            stufe_bei_abweichung:
              art === 'messwert' ? text('stufe_bei_abweichung') || null : null,
          }

          if (schritt) {
            aendern.mutate({ id: schritt.id, ...nutzlast }, { onSuccess: schliessen })
          } else {
            anlegen.mutate(
              { ...nutzlast, art, sortierung: naechsteSortierung },
              { onSuccess: schliessen },
            )
          }
        }}
      >
        {!schritt && (
          <div>
            <label htmlFor="s-art">Art</label>
            <select
              id="s-art"
              value={art}
              onChange={(e) => setzeArt(e.target.value as Schrittart)}
            >
              {ARTEN.map((a) => (
                <option key={a} value={a}>
                  {SCHRITTARTTEXT[a]}
                </option>
              ))}
            </select>
            {/* Die Erklärung steht unter der Auswahl und wechselt mit ihr.
                Ohne sie ist der Unterschied zwischen Sichtprüfung und
                Bestätigung nicht zu erraten – und er entscheidet darüber,
                ob eine Mängelstufe vorgeschlagen wird. */}
            <div className="mt-2 rounded-feld border-2 border-slate-200 bg-slate-50 p-3">
              <p className="font-semibold">{SCHRITTARTTEXT[art]}</p>
              <p className="mt-1 text-base">{SCHRITTARTHILFE[art].was}</p>
              <p className="mt-1 text-base text-slate-700">
                <span className="font-semibold">Wirkung:</span>{' '}
                {SCHRITTARTHILFE[art].folge}
              </p>
            </div>
            <p className="mt-1 text-base text-slate-600">
              Die Art lässt sich später nicht mehr ändern – ein Messschritt hat
              Sollwerte, ein Sichtschritt nicht.
            </p>
          </div>
        )}

        <div>
          <label htmlFor="s-titel">Titel</label>
          <input
            id="s-titel"
            name="titel"
            defaultValue={schritt?.titel ?? ''}
            required
            maxLength={200}
          />
        </div>

        <div>
          <label htmlFor="s-hinweis">Hinweis für den Prüfer</label>
          <textarea
            id="s-hinweis"
            name="hinweis"
            defaultValue={schritt?.hinweis ?? ''}
            rows={2}
          />
        </div>

        {art === 'messwert' && (
          <div className="rounded-feld border-2 border-slate-200 p-3">
            <p className="mb-2 font-semibold">Messung</p>
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <label htmlFor="s-groesse">Messgröße</label>
                <input
                  id="s-groesse"
                  name="messgroesse"
                  defaultValue={schritt?.messgroesse ?? ''}
                  placeholder="z. B. Schichtdicke Fallschutz"
                />
              </div>
              <div>
                <label htmlFor="s-einheit">Einheit</label>
                <input
                  id="s-einheit"
                  name="einheit"
                  defaultValue={schritt?.einheit ?? ''}
                  placeholder="cm"
                />
              </div>
              <div>
                <label htmlFor="s-min">Sollwert mindestens</label>
                <input
                  id="s-min"
                  name="sollwert_min"
                  type="number"
                  step="any"
                  defaultValue={schritt?.sollwert_min ?? ''}
                />
              </div>
              <div>
                <label htmlFor="s-max">Sollwert höchstens</label>
                <input
                  id="s-max"
                  name="sollwert_max"
                  type="number"
                  step="any"
                  defaultValue={schritt?.sollwert_max ?? ''}
                />
              </div>
              <div className="sm:col-span-2">
                <label htmlFor="s-stufe">Stufe bei Abweichung</label>
                <select
                  id="s-stufe"
                  name="stufe_bei_abweichung"
                  defaultValue={schritt?.stufe_bei_abweichung ?? ''}
                >
                  <option value="">— kein Vorschlag —</option>
                  {STUFEN.map((s) => (
                    <option key={s} value={s}>
                      {s} – {STUFENTEXT[s]}
                    </option>
                  ))}
                </select>
                <p className="mt-1 text-base text-slate-600">
                  Mindestens eine Grenze ist Pflicht: ohne Sollbereich kann der Schritt
                  keine Abweichung erkennen.
                </p>
              </div>
            </div>
          </div>
        )}

        <div>
          <label htmlFor="s-norm">Normverweis</label>
          <input
            id="s-norm"
            name="normverweis"
            defaultValue={schritt?.normverweis ?? ''}
            maxLength={60}
            placeholder="DIN EN 1176-2"
          />
        </div>

        <label className="flex items-start gap-3">
          <input
            type="checkbox"
            name="pflicht"
            defaultChecked={schritt?.pflicht ?? true}
            className="mt-1 h-6 w-6"
          />
          <span className="font-normal">
            <span className="font-semibold">Pflichtschritt</span>
            <span className="block text-base text-slate-600">
              Ohne ihn lässt sich der Ablauf nicht abschließen.
            </span>
          </span>
        </label>

        <Fehlerhinweis fehler={anlegen.error} />
        <Fehlerhinweis fehler={aendern.error} />
      </form>

      {schritt && (
        <div className="mt-6 border-t-2 border-slate-200 pt-4">
          <button
            type="button"
            className="knopf-warnung"
            disabled={laeuft}
            onClick={() => {
              if (window.confirm(`Schritt „${schritt.titel}" entfernen?`)) {
                loeschen.mutate(schritt.id, { onSuccess: schliessen })
              }
            }}
          >
            Schritt entfernen
          </button>
          <p className="mt-1 text-base text-slate-600">
            Bereits durchgeführte Prüfungen behalten ihre Ergebnisse.
          </p>
        </div>
      )}
    </Blatt>
  )
}
